import { Controller } from "react-hook-form";
import toast from "@/components/ui/toast";
import Notification from "@/components/ui/Notification";
import PasswordInput from "../../../components/shared/PasswordInput";
import useFormServerBacked from "@/custolink-hooks/form/useFormServerBacked";
import {
  PublicAssetFrontendUserApi,
  PublicAssetFrontendUserApi$Signup,
} from "@/custolink-transpiled_by_server/bc.asset_interaction.api_methods";
import useUiContext from "@/custolink-hooks/useUiContext";
import useFormErrorPicker from "@/custolink-hooks/form/useFormErrorPicker";
import { useState } from "react";
import { rdaNotify } from "@/custolink-views/asset_frontend/redesigns/RdaToast";
import { useTranslation } from "react-i18next";
import { useAssetFrontendDesign } from "@/custolink-views/asset_frontend/redesigns/assetFrontendDesign";
import {
  FormField,
  PasswordField,
  PrimaryButton,
  SecondaryButton,
  TextField,
} from "@/custolink-views/asset_frontend/redesigns/formKit";

// Shared with the classic design (same AccountDrawer content as SignInForm), so the success
// toast and the password fields follow the asset-frontend switch; the rest of this file was
// already redesigned unconditionally by an earlier batch.
export default function SignupForm(p: { back: () => void }) {
  const uic = useUiContext();
  const { t } = useTranslation();
  const newDesign = useAssetFrontendDesign();
  const [mismatch, setMismatch] = useState(false);
  const form = useFormServerBacked<
    PublicAssetFrontendUserApi$Signup & {
      repeatpassword: string;
      consent: boolean;
    }
  >(
    {
      email: "",
      password: "",
      repeatpassword: "",
      name: "",
      consent: true,
    },
    (d) => {
      if (d.password != d.repeatpassword) {
        setMismatch(true);
        throw new Error("Passwords do not match");
      } else {
        setMismatch(false);
        return PublicAssetFrontendUserApi.signup(
          uic.organisationContext.organisationId,
          {
            email: d.email,
            password: d.password,
            name: d.name,
          },
        );
      }
    },
    () => {
      const message = t("account.signUp.success.checkEmail.isSignedOnNow");
      if (newDesign) {
        rdaNotify.success(message);
      } else {
        toast.push(<Notification title={message} type="success" />, { placement: "top-center" });
      }
      setTimeout(() => {
        window.location.reload();
      }, 500);
    },
    undefined,
    "asset",
  );

  const e = useFormErrorPicker(form.errors, "email", "name", "password");

  return (
    <form onSubmit={form.handleSubmit()}>
      <div className="space-y-4">
        <FormField label={t("account.signUp.fullName")} error={e.name}>
          <Controller
            name="name"
            control={form.control}
            render={({ field }) => (
              <TextField
                type="text"
                placeholder={t("account.signUp.fullName")}
                autoComplete="off"
                invalid={Boolean(e.name)}
                {...field}
              />
            )}
          />
        </FormField>

        <FormField label={t("account.signUp.email")} error={e.email}>
          <Controller
            name="email"
            control={form.control}
            render={({ field }) => (
              <TextField
                type="email"
                placeholder={t("account.signUp.email")}
                autoComplete="off"
                invalid={Boolean(e.email)}
                {...field}
              />
            )}
          />
        </FormField>

        <FormField label={t("account.signUp.password")} error={e.password}>
          <Controller
            name="password"
            control={form.control}
            rules={{ required: true }}
            render={({ field }) =>
              newDesign ? (
                <PasswordField
                  placeholder={t("account.signUp.password")}
                  autoComplete="off"
                  {...field}
                />
              ) : (
                <PasswordInput
                  type="text"
                  placeholder={t("account.signUp.password")}
                  autoComplete="off"
                  {...field}
                />
              )
            }
          />
        </FormField>

        <FormField
          label={t("account.signUp.repeatPassword")}
          error={mismatch ? "Passwords do not match" : undefined}
        >
          <Controller
            name="repeatpassword"
            control={form.control}
            rules={{ required: true }}
            render={({ field }) =>
              newDesign ? (
                <PasswordField
                  placeholder={t("account.signUp.password")}
                  autoComplete="off"
                  {...field}
                />
              ) : (
                <PasswordInput
                  type="text"
                  placeholder={t("account.signUp.password")}
                  autoComplete="off"
                  {...field}
                />
              )
            }
          />
        </FormField>

        <PrimaryButton type="submit" disabled={!form.isDirty || form.isSubmitting}>
          {t("account.signUp.signUp")}
        </PrimaryButton>

        <SecondaryButton className="w-full" type="button" onClick={p.back}>
          {t("account.signUp.back")}
        </SecondaryButton>
      </div>
    </form>
  );
}
